/* eslint-disable camelcase */
import React, { useCallback, useEffect, useRef } from 'react';
import AdminSessionWarningModal from '~/components/modules/AdminSessionWarningModal';
import { getAdminAccess } from '~/api/adminMfa';
import { setConfig } from '~/contexts/stores/configStore';
import {
    setAdminMfaCheckFailed,
    setAdminMfaChecking,
    setAdminMfaRequired,
    setAdminMfaSession,
} from '~/redux/modules/adminMfa';
import { clearModalContent, setModalContent } from '~/redux/modules/modalEvent';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import { applyAdminMfaToken, subscribeToAdminSessionChanges } from './useAdminSessionActions';
import useAdminSessionCountdown from './useAdminSessionCountdown';
import useAdminSessionNotification from './useAdminSessionNotification';
import useAdminSessionActivity from './useAdminSessionActivity';
import {
    armWarningThresholds,
    consumeWarningThresholds,
    dueWarningThreshold,
} from '~/utils/adminSessionWarnings';

/**
 * The app-wide admin session watcher: it decides *when* the warning shows, ends
 * the session when it lapses, refreshes on admin activity, and keeps other tabs
 * in step.
 *
 * Mounted once from App.tsx beside the other global watchers, and gated on the
 * session being valid rather than on the current route. That placement is the
 * point: outside Admin nothing extends the session, so the member most likely
 * to be holding one that is about to lapse is the one who has wandered off to
 * another part of the app. Inside withAdminSideBar this would never reach them.
 *
 * What it deliberately does *not* own is the warning's contents. The modal is
 * dispatched into the modal slice once and counts down from inside itself, so
 * nothing here re-renders per second and no JSX is rewritten into redux on a
 * timer.
 *
 * Nothing here navigates either: an expiry sets the status and the Admin
 * screens react to it.
 *
 * @returns {Object} remainingSeconds, isWarningDue and a recompute callback
 */
const useAdminSessionWatcher = () => {
    const dispatch = useAppDispatch();

    const { id } = useAppSelector(({ profile }) => profile);
    const { status, expiresAt, expiresInSeconds } = useAppSelector(({ adminMfa }) => adminMfa);
    const { isAuthed } = useAppSelector(({ authentication }) => authentication);
    const { modalContent } = useAppSelector(({ modalEvent }) => modalEvent);

    // 30-second resolution is all this needs: it only has to notice when a
    // threshold is crossed. The modal reads the same stored deadline at
    // 1-second resolution, so the two cannot disagree about when it ends.
    const {
        remainingSeconds,
        isWarningDue,
        isExpired,
        thresholds,
        onRecompute,
    } = useAdminSessionCountdown();

    // Which rungs are still to fire, for which deadline. A ref, not state:
    // firing a warning must not itself cause a render, or the effect below
    // re-enters on its own output.
    const armedRef = useRef<{ deadline?: string; pending: number[] }>({ pending: [] });

    useAdminSessionActivity(remainingSeconds, isWarningDue);

    // Driven from the rung decision below rather than watching the clock
    // itself, so the notification and the modal can never disagree about which
    // warning was reached, and every rung notifies instead of only the first.
    const { onNotify } = useAdminSessionNotification(isWarningDue);

    const isExpiringRef = useRef(false);

    /**
     * Whether the modal slot currently holds *our* warning.
     *
     * modalEvent is a single slot shared with the whole app and carries no
     * identity, so "clear the warning" and "clear whatever is open" are the same
     * dispatch. This hook is mounted app-wide, which makes that a real
     * collision: a signed-in member on one of the 38 unauthenticated pathway
     * routes can have the member feedback modal open on /summary while their
     * admin status resolves to 'required' underneath, and an unconditional
     * clear would shut it.
     *
     * Derived from the slot rather than tracked in a ref, because a ref goes
     * stale the moment anything else closes or replaces the modal — dismissing
     * the warning from inside itself does exactly that — and a stale "it's
     * ours" is the same bug again.
     */
    const isOurWarningUp =
        React.isValidElement(modalContent) && modalContent.type === AdminSessionWarningModal;

    /**
     * Reaching zero is a signal to ask the server, never a conclusion. A
     * throttled background tab is routinely a minute or more out, and ejecting
     * a member whose session is in fact alive would be worse than being late.
     *
     * But the screens must not keep rendering while that question is out.
     * 'checking' goes in first, before the request: the gate reads it as
     * unresolved and swaps the page for its spinner immediately, so the member
     * never sees admin content over a session that has run out. It is not a
     * verdict — the answer still decides — it only stops the old one being
     * shown as though it still held.
     *
     * That is why the status is 'checking' and not 'required'. Concluding here
     * would flash "Verification required" at a member whose session another tab
     * had just extended, and then take it away again.
     */
    const onExpire = useCallback(async () => {
        if (isExpiringRef.current || !id) return;

        isExpiringRef.current = true;

        dispatch(setAdminMfaChecking());

        try {
            const response = await getAdminAccess(id);
            const mfa = response?.mfa || {};

            if (mfa.valid) {
                // Another tab extended it, or a slide landed after our last
                // read. Adopt the server's deadline and carry on.
                applyAdminMfaToken(mfa);
                dispatch(setAdminMfaSession(mfa));

                return;
            }

            setConfig({ ADMIN_MFA_TOKEN: undefined });
            dispatch(setAdminMfaRequired());

            if (isOurWarningUp) dispatch(clearModalContent());

            // No navigation. Setting the status to 'required' is the whole
            // ending: on an Admin screen the modal in AdminSidebarContainer
            // picks it up and asks for a code over the page the member is
            // already on, and anywhere else in the app nothing happens at all,
            // which is right — they were not using Admin.
        } catch (error) {
            // Unanswered, not refused. 'checking' is the status by now and the
            // gate is showing its spinner, so leaving it would spin for ever.
            // Resolving it to 'error' keeps the screens shut — a check that did
            // not come back must never read as "session valid" — while giving
            // the member a retry rather than a code prompt they cannot satisfy
            // over a connection that just failed.
            setConfig({ ADMIN_MFA_TOKEN: undefined });
            dispatch(setAdminMfaCheckFailed());

            if (isOurWarningUp) dispatch(clearModalContent());
        } finally {
            isExpiringRef.current = false;
        }
    }, [dispatch, id, isOurWarningUp]);

    // Cross-tab sync. Two admin tabs each run their own countdown against one
    // server-side session, so an extend in one has to silence the other —
    // otherwise it notifies about an expiry that already moved and then bounces
    // a member whose session is alive.
    useEffect(() => {
        if (!isAuthed) return;

        // Shares the broadcaster's channel object, so this tab is never woken
        // by its own message — only by another tab's.
        return subscribeToAdminSessionChanges(() => {
            // Re-read rather than trust the payload: the server is the
            // authority on the deadline, and this is one cheap request.
            if (id) {
                getAdminAccess(id)
                    .then(response => {
                        const mfa = response?.mfa || {};

                        applyAdminMfaToken(mfa);
                        dispatch(setAdminMfaSession(mfa));
                    })
                    .catch(() => undefined);
            }
        });
    }, [dispatch, id, isAuthed]);

    useEffect(() => {
        if (isExpired) onExpire();
    }, [isExpired, onExpire]);

    /**
     * Arm the ladder for whichever deadline is current.
     *
     * Rungs already behind the deadline when it was issued are dropped rather
     * than queued. That is what stops a session pinned at its 12-hour ceiling
     * from warning again the moment it is extended: the extend returns a
     * deadline with, say, four minutes on it, every rung is already behind it,
     * and nothing fires.
     *
     * Armed from expiresInSeconds — the server's own measure of the life this
     * deadline was issued with — and *not* from the live countdown. The
     * countdown's state is written by an effect in useAdminSessionCountdown, so
     * in the commit where a new deadline arrives it still holds the previous
     * value; on the first session of a page it holds 0, which armed an empty
     * ladder and produced no warnings at all before the session lapsed. The
     * value this rule wants is a property of the deadline, and it arrives in
     * the same render as the deadline itself.
     */
    useEffect(() => {
        if (status !== 'valid' || !expiresAt) {
            armedRef.current = { pending: [] };

            return;
        }

        if (armedRef.current.deadline === expiresAt) return;

        armedRef.current = {
            deadline: expiresAt,
            pending: armWarningThresholds(thresholds, expiresInSeconds),
        };
    }, [expiresAt, expiresInSeconds, status, thresholds]);

    /**
     * Fire the outermost rung the clock has now passed.
     *
     * Showing a warning must not extend anything — only its button does.
     * Otherwise the warning renews the session it exists to announce and the
     * expiry becomes unreachable.
     *
     * Each rung is dispatched once and removed. The modal cannot be closed, so
     * the later rungs no longer exist to re-reach someone who dismissed an
     * earlier one — they exist for the member who is away from the tab, where
     * every rung raises its own notification. The element is not re-dispatched
     * as the clock runs either; it holds its own countdown.
     */
    useEffect(() => {
        if (status !== 'valid' || !isWarningDue) return;

        const { pending } = armedRef.current;

        if (dueWarningThreshold(pending, remainingSeconds) === undefined) return;

        armedRef.current = {
            ...armedRef.current,
            pending: consumeWarningThresholds(pending, remainingSeconds),
        };

        dispatch(
            setModalContent({
                title: 'Session ending',
                size: 'sm',
                // No way out but a decision: keep working, or lock. The header's
                // close button is gone, and because the base Modal wires onHide
                // to a no-op whenever this is set, the backdrop and Escape stop
                // dismissing it too.
                disableClose: true,
                modalContent: <AdminSessionWarningModal />,
            })
        );

        // Only lands if they are actually away — onNotify checks that itself, at
        // this moment rather than when the rung was armed.
        onNotify(remainingSeconds);
    }, [dispatch, isWarningDue, onNotify, remainingSeconds, status]);

    // Our warning has nothing left to say: take it down. Covers the session
    // ending, and an extend from anywhere — the button inside the warning,
    // another tab, or the member simply resuming work.
    //
    // !isWarningDue is the load-bearing part. Without it only the button's own
    // handler took the modal down, because it calls onDismiss itself. Now that
    // ordinary activity slides the session, an idle member who returns to their
    // desk and types would otherwise be left staring at a warning counting down
    // from two hours.
    //
    // Only ever clears a warning this hook put up — see isOurWarningUp. The
    // isAuthed guard is the outer one: on an unauthenticated route the status is
    // permanently 'unknown', so the condition below is permanently true and
    // there is nothing here worth running at all.
    useEffect(() => {
        if (!isAuthed) return;

        if (status !== 'valid' || isExpired || !isWarningDue) {
            if (isOurWarningUp) dispatch(clearModalContent());
        }
    }, [dispatch, isAuthed, isExpired, isOurWarningUp, isWarningDue, status]);

    return { remainingSeconds, isWarningDue, onRecompute };
};

export default useAdminSessionWatcher;
